import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { tqQueryKeys } from "lib/constants";
import { EvendyApi } from "lib/utils";
import { ISODateString } from "lib/types";

type OrganizerUser = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  userType: string;
  password: string;
  verified: boolean;
  deletedAt: ISODateString | null;
  createdAt: ISODateString;
  updatedAt: ISODateString;
  lastLogin: ISODateString;
};

type OrganizerOrganization = {
  id: number;
  name: string;
  description: string;
  category: string;
  country: string;
  state: string;
  city: string;
  address: string;
  createdAt: ISODateString;
  updatedAt: ISODateString;
  organizerEmail: string;
  organizers: string[];
} | null;

export type OrganizerData = {
  id: number;
  userEmail: string;
  avatar: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  address: string | null;
  workAddress: string | null;
  kycLevel: string;
  role: string;
  accountStatus: string;
  organizationName: string | null;
  deletedAt: ISODateString | null;
  createdAt: ISODateString;
  updatedAt: ISODateString;
  user: OrganizerUser;
  organization: OrganizerOrganization;
};

type GetOrganizerResponse = {
  data: OrganizerData[];
  page: number;
  limit: number;
  totalCount: number;
};

type UseGetOrganizerParams = {
  page: number;
  limit: number;
  search?: string;
};

type UseGetOrganizerResult = UseQueryResult<GetOrganizerResponse, Error>;

export function useGetOrganizer(params: UseGetOrganizerParams): UseGetOrganizerResult {
  const { page, limit, search } = params;

  return useQuery({
    queryKey: [tqQueryKeys.organizer.getOrganizer, page, limit, search],
    queryFn: async (): Promise<GetOrganizerResponse> => {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
      });

      const res = await EvendyApi.get<GetOrganizerResponse>(`organizer?${queryParams.toString()}`);
      return res.data;
    },
  });
}
